import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  ButtonBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Typography,
  Tooltip,
  Divider
} from '@material-ui/core';
import TaskIcon from '@mui/icons-material/Task';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import useAuth from '../../hooks/useAuth';
import ScrollJumpButtons from '../ScrollJumpButtons';
import { sessionApi } from '../../api/sessionApi';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, greyAlpha, grey } from '../../theme/tones';

const TaskNotificationsPopover = () => {
  const anchorRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await sessionApi.fetchTaskNotifications(localStorage.getItem('accessToken'));
      if (data) {
        setNotifications(data);
        setUnreadCount(data.filter(n => Number(n.is_read) === 0).length);
      }
    } catch (err) {
      console.error('Erreur de récupération des notifications de tâches :', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Pool notifications every 60 seconds for live updates
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const getListElement = useCallback(() => listRef.current, []);

  const handleOpen = () => {
    setOpen(true);
    fetchNotifications();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (Number(notif.is_read) === 0) {
        // Mark as read in DB
        await sessionApi.readNotification(notif.id, localStorage.getItem('accessToken'));
        
        // Update local state
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: 1 } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      handleClose();

      if (notif.type === 'task') {
        // Redirect to session details page with tasks tab activated
        navigate(`/dashboard/session/${notif.session_id}?tab=tasks`);

        // Trigger a window reload or route refresh if already on the page
        if (window.location.pathname === `/dashboard/session/${notif.session_id}`) {
          window.location.search = '?tab=tasks';
        }
      } else {
        // Notifications non liées à une tâche (désinscription, réclamation) : lien direct
        navigate(notif.link || '/dashboard');
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <Box
          component={ButtonBase}
          onClick={handleOpen}
          ref={anchorRef}
          sx={{
            alignItems: 'center',
            display: 'flex',
            position: 'relative',
            p: 1.2,
            borderRadius: '12px',
            bgcolor: open ? toneAlpha('indigo', 0.08) : 'transparent',
            color: open ? 'primary.main' : 'text.secondary',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.tones.indigo.base, 0.06),
              color: 'primary.main',
            }
          }}
        >
          <TaskIcon sx={{ fontSize: 24 }} />
          {unreadCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 18,
                height: 18,
                backgroundColor: 'error.main',
                borderRadius: '50%',
                border: (theme) => `2px solid ${theme.palette.common.white}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: grey(0),
                fontSize: '0.65rem',
                fontWeight: 800,
                boxShadow: `0 2px 4px ${toneAlpha('rose', 0.3)}`,
              }}
            >
              {unreadCount}
            </Box>
          )}
        </Box>
      </Tooltip>

      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            width: 330,
            borderRadius: '16px',
            boxShadow: `0 12px 30px ${greyAlpha(900, 0.08)}`,
            border: `1px solid ${greyAlpha(200, 0.8)}`,
            mt: 1,
            p: 1.5,
          }
        }}
      >
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" sx={{ fontWeight: 700, bgcolor: (theme) => alpha(theme.palette.tones.indigo.base, 0.08), color: 'primary.main', px: 1, py: 0.25, borderRadius: '8px' }}>
              {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 1 }} />

        {notifications.length === 0 ? (
          <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
              Aucune notification pour le moment.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            <ScrollJumpButtons
              getTarget={getListElement}
              watch={`${open}-${notifications.length}`}
              placement="edges"
              offset={6}
            />
            <List
              disablePadding
              ref={listRef}
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, maxHeight: 350, overflowY: 'auto' }}
            >
            {notifications.map((notif) => {
              const isUnread = Number(notif.is_read) === 0;
              const isTask = notif.type === 'task';
              const isDesinscription = notif.type === 'desinscription';
              const isReclamation = notif.type === 'reclamation';
              // Ligne de checklist validée : le message porte déjà le contrôle,
              // la formation et le numéro de session.
              const isChecklist = notif.type === 'checklist';
              // Alerte du premier jour ouvré : inscriptions du week-end ou
              // d'un férié qui attendent leur confirmation.
              const isConfirmation = notif.type === 'confirmation';
              // Convention restée non signée après les trois relances.
              const isAgreement = notif.type === 'convention';
              // Décision J-30 : maintenir ou annuler la session.
              const isDecision = notif.type === 'decision_j30';
              // Alerte RH : échéance légale ou décision en attente. Celles qui
              // sont déjà en retard se distinguent — c'est tout l'intérêt de
              // les sortir de l'écran où elles dormaient.
              const isRh = notif.type === 'rh';
              const isRhRetard = isRh && String(notif.title || '').startsWith('En retard');

              // Configuration d'affichage selon le type de notification.
              const accent = isDesinscription
                ? getTone('rose').color
                : isReclamation
                  ? getTone('amber').color
                  : isChecklist
                    ? getTone('emerald').color
                    : isConfirmation
                      ? getTone('sky').color
                      : isAgreement
                        ? getTone('amber').color
                        : isDecision
                          ? getTone('violet').color
                          : isRhRetard
                            ? getTone('rose').color
                            : isRh ? getTone('teal').color : getTone('indigo').base;
              const accentBg = isDesinscription
                ? toneAlpha('rose', 0.1)
                : isReclamation
                  ? toneAlpha('amber', 0.1)
                  : isChecklist
                    ? toneAlpha('emerald', 0.1)
                    : isConfirmation
                      ? toneAlpha('sky', 0.1)
                      : isAgreement
                        ? toneAlpha('amber', 0.1)
                        : isDecision
                          ? toneAlpha('violet', 0.1)
                          : isRhRetard
                            ? toneAlpha('rose', 0.1)
                            : isRh ? toneAlpha('teal', 0.1) : toneAlpha('indigo', 0.1);
              const icon = isDesinscription
                ? <PersonRemoveIcon sx={{ fontSize: '1.1rem' }} />
                : isReclamation
                  ? <PriorityHighIcon sx={{ fontSize: '1.1rem' }} />
                  : isChecklist
                    ? <CheckCircleOutlineIcon sx={{ fontSize: '1.1rem' }} />
                    : isConfirmation
                      ? <MarkEmailReadIcon sx={{ fontSize: '1.1rem' }} />
                      : isRh
                        ? <BadgeIcon sx={{ fontSize: '1.1rem' }} />
                        : <AssignmentIcon sx={{ fontSize: '1.1rem' }} />;
              const title = isDesinscription
                ? (notif.title || 'Désinscription')
                : isReclamation
                  ? (notif.title || 'Affectation de réclamation')
                  : isChecklist
                    ? (notif.title || 'Checklist')
                    // Tout autre type apporte son propre titre : une
                    // notification inconnue ne doit pas s'annoncer comme une
                    // affectation de tâche.
                    : (isTask ? 'Affectation de tâche' : (notif.title || 'Notification'));

              return (
                <ListItem
                  key={notif.id}
                  button
                  onClick={() => handleNotificationClick(notif)}
                  sx={{
                    borderRadius: '10px',
                    py: 1.2,
                    px: 1.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    bgcolor: isUnread ? toneAlpha('indigo', 0.02) : 'transparent',
                    border: '1px solid',
                    borderColor: isUnread ? toneAlpha('indigo', 0.06) : greyAlpha(200, 0.5),
                    '&:hover': {
                      bgcolor: isUnread ? toneAlpha('indigo', 0.06) : greyAlpha(900, 0.02),
                      borderColor: isUnread ? toneAlpha('indigo', 0.1) : greyAlpha(200, 0.8),
                    }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 32, mt: 0.25 }}>
                    <Avatar sx={{ bgcolor: isUnread ? accentBg : toneAlpha('neutral', 0.08), color: isUnread ? accent : 'text.secondary', width: 32, height: 32 }}>
                      {icon}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: isUnread ? 'text.primary' : 'text.secondary', lineHeight: 1.3 }}>
                          {title}
                        </Typography>
                        <Box sx={{
                          px: 1,
                          py: 0.25,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          borderRadius: '6px',
                          bgcolor: isUnread ? getTone('rose').color : toneAlpha('neutral', 0.08),
                          color: isUnread ? grey(0) : 'text.secondary',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap'
                        }}>
                          {isUnread ? 'nouveau' : 'lu'}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {isTask ? (
                          <>
                            <Typography sx={{ fontSize: '0.8rem', color: isUnread ? 'text.secondary' : 'text.disabled', lineHeight: 1.4 }}>
                              Vous avez été affecté à la tâche <strong>{notif.task_name}</strong>.
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: isUnread ? 'primary.main' : 'text.disabled', fontWeight: 600, mt: 0.5 }}>
                              Session #{notif.number_session}
                            </Typography>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: '0.8rem', color: isUnread ? 'text.secondary' : 'text.disabled', lineHeight: 1.4 }}>
                            {notif.message}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
            </List>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default TaskNotificationsPopover;
